import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup } from '@/components/ui/radioGroup';
import { RadioGroupItemWithLabel } from '@/components/ui/radioGroupItemWithLabel';
import { Combobox } from '@/components/ui/combobox';
import Flag from 'react-world-flags';
import { extractIbanCountryCode, extractValidSwiftCodeCountryCode } from '@app/domain/accountsDetailsHelpers';
import COUNTRIES, { COUNTRY_CODES, getCountryByCountryCode } from '@app/domain/countries';
import {
  SUPPORTED_VERIFICATION_DOMESTIC_COUNTRIES_ARRAY,
  VERIFICATION_ACCOUNT_TYPE,
  getAccountValidationFields,
  AccountVerificationField,
} from '@app/domain/accountVerification';
import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { accountVerificationSchema, AccountVerificationForm } from './searchPayeeUtils';
import PayeeSearchInput from '@/components/ui/PayeeSearchInput';
import KnoxOrTaxIdSearchInput from '@/components/ui/KnoxOrTaxIdSearchInput';
import { UnifiedPayee } from '@mortee/domain/morteeAccountVerificationUnifiedPayee';

export default function AccountVerificationInitialPageV2({
  showResult,
  persistedData,
  setSelectedPayee,
  onSubmitForm,
  setCountryCode,
}: {
  showResult: (show: boolean) => void;
  persistedData: AccountVerificationForm;
  setSelectedPayee: (payee: UnifiedPayee | null) => void;
  onSubmitForm: (data: AccountVerificationForm) => Promise<void>;
  setCountryCode: (countryCode: string) => void;
}): JSX.Element {
  const form = useForm<AccountVerificationForm>({
    resolver: zodResolver(accountVerificationSchema),
    defaultValues: persistedData,
  });

  const { setValue, watch, formState } = form;
  const { isSubmitting } = formState;

  const accountType = watch('accountType');
  const selectedCountryName = watch('country');
  const currentIban = watch('iban');
  const currentSwiftBic = watch('swiftBic');
  const ibanCountry = React.useMemo(() => extractIbanCountryCode(currentIban), [currentIban]);
  const swiftCountry = React.useMemo(() => extractValidSwiftCodeCountryCode(currentSwiftBic), [currentSwiftBic]);
  const countryInfo = React.useMemo(
    () => getCountryByCountryCode(accountType === VERIFICATION_ACCOUNT_TYPE.iban ? ibanCountry : swiftCountry),
    [accountType, ibanCountry, swiftCountry],
  );

  React.useEffect(() => {
    if (countryInfo) {
      const countryCode = accountType === VERIFICATION_ACCOUNT_TYPE.iban ? ibanCountry : swiftCountry;
      setCountryCode(countryCode?.toUpperCase() || '');
    }
  }, [countryInfo, setCountryCode, accountType, ibanCountry, swiftCountry]);

  const [dynamicDomesticFields, setDynamicDomesticFields] = React.useState<
    Array<{ key: string; config: AccountVerificationField }>
  >([]);

  const getCountryCodeByNameLocal = React.useCallback((name: string): string | undefined => {
    if (!name) return undefined;
    const upperCaseName = name.toUpperCase();
    for (const code in COUNTRIES) {
      if (COUNTRIES[code as COUNTRY_CODES]?.name.toUpperCase() === upperCaseName) {
        return code;
      }
    }
    return undefined;
  }, []);

  React.useEffect(() => {
    const newFieldsToRender: Array<{ key: string; config: AccountVerificationField }> = [];
    const newFieldKeys: Set<string> = new Set();

    if (accountType === VERIFICATION_ACCOUNT_TYPE.domestic && selectedCountryName) {
      const countryCode = getCountryCodeByNameLocal(selectedCountryName);
      if (countryCode) {
        setCountryCode(countryCode);
        const fieldsConfig = getAccountValidationFields(countryCode);
        if (fieldsConfig) {
          if (fieldsConfig.bankCode) {
            newFieldsToRender.push({ key: 'bankCode', config: fieldsConfig.bankCode });
            newFieldKeys.add('bankCode');
          }
          if (fieldsConfig.branchCode) {
            newFieldsToRender.push({ key: 'branchCode', config: fieldsConfig.branchCode });
            newFieldKeys.add('branchCode');
          }
          if (fieldsConfig.accountNumber) {
            newFieldsToRender.push({ key: 'accountNumber', config: fieldsConfig.accountNumber });
            newFieldKeys.add('accountNumber');
          }
        }
      }
    }

    const previousFieldKeys = dynamicDomesticFields.map((df) => df.key);
    previousFieldKeys.forEach((prevKey) => {
      if (!newFieldKeys.has(prevKey)) {
        setValue(prevKey as keyof AccountVerificationForm, '');
      }
    });

    if (accountType !== VERIFICATION_ACCOUNT_TYPE.domestic) {
      previousFieldKeys.forEach((prevKey) => {
        setValue(prevKey as keyof AccountVerificationForm, '');
      });
    }

    setDynamicDomesticFields(newFieldsToRender);
  }, [accountType, selectedCountryName, setValue, dynamicDomesticFields, getCountryCodeByNameLocal, setCountryCode]);

  const handleGenericPayeeSelection = (payee: UnifiedPayee): void => {
    setSelectedPayee(payee);

    const payeeNameValue = payee.names?.[0]?.name || '';
    const knoxId = payee.strongLegalIdentifies?.KnoxId?.[0]?.value || '';

    setValue('payeeName', payeeNameValue);
    setValue('knoxOrTaxId', knoxId);
  };

  const handleFormSubmit = async (data: AccountVerificationForm): Promise<void> => {
    // Clean not relevant fields based on accountType
    if (data.accountType === VERIFICATION_ACCOUNT_TYPE.iban) {
      data.accountNumber = '';
      data.swiftBic = '';
      data.country = '';
      data.bankCode = '';
      data.branchCode = '';
      setValue('accountNumber', '');
      setValue('swiftBic', '');
      setValue('country', '');
      setValue('bankCode', '');
      setValue('branchCode', '');
    } else if (data.accountType === VERIFICATION_ACCOUNT_TYPE.swift) {
      data.iban = '';
      setValue('iban', '');
    } else if (data.accountType === VERIFICATION_ACCOUNT_TYPE.domestic) {
      data.iban = '';
      data.swiftBic = '';
      setValue('iban', '');
      setValue('swiftBic', '');
    }
    await onSubmitForm(data);
  };

  return (
    <div className='min-h-screen bg-background'>
      <div className='w-full max-w-2xl mx-auto p-6 space-y-6 pt-24'>
        <div className='space-y-2'>
          <h1 className='text-2xl font-semibold'>Search for a payee</h1>
        </div>
        <Form {...form}>
          <form data-testid='search-payee-form' onSubmit={form.handleSubmit(handleFormSubmit)} className='space-y-6'>
            <FormField
              data-testid='payeeName'
              control={form.control}
              name='payeeName'
              render={({ field }): JSX.Element => (
                <FormItem>
                  <FormLabel>Payee name</FormLabel>
                  <FormControl>
                    <PayeeSearchInput
                      placeholder='Type or search a payee'
                      value={field.value}
                      inputClassName='!text-black'
                      onValueChange={(value): void => {
                        field.onChange(value);
                        if (!value) setSelectedPayee(null);
                      }}
                      onPayeeSelected={handleGenericPayeeSelection}
                      onManualEditAfterSelection={(): void => {
                        form.setValue('knoxOrTaxId', '');
                        if (accountType === VERIFICATION_ACCOUNT_TYPE.domestic) {
                          form.setValue('country', '');
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              data-testid='knoxOrTaxId'
              control={form.control}
              name='knoxOrTaxId'
              render={({ field }): JSX.Element => (
                <FormItem>
                  <FormLabel>KnoxID / Tax ID</FormLabel>
                  <FormControl>
                    <KnoxOrTaxIdSearchInput
                      placeholder='Search by Knox ID or Tax ID'
                      value={field.value}
                      inputClassName='!text-black'
                      searchCondition={(query): boolean => query.toUpperCase().startsWith('KNOX')}
                      onValueChange={(value): void => {
                        if (value && !value.toUpperCase().startsWith('KNOX')) {
                          setValue('taxId', value);
                        }
                        field.onChange(value);
                        if (!value) setSelectedPayee(null);
                      }}
                      onPayeeSelected={handleGenericPayeeSelection}
                      onManualEditAfterSelection={(): void => {
                        form.setValue('payeeName', '');
                        if (accountType === VERIFICATION_ACCOUNT_TYPE.domestic) {
                          form.setValue('country', '');
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              data-testid='accountType'
              control={form.control}
              name='accountType'
              render={({ field }): JSX.Element => (
                <FormItem className='space-y-3'>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className='flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4'
                    >
                      <RadioGroupItemWithLabel
                        value={VERIFICATION_ACCOUNT_TYPE.domestic}
                        data-testid='domestic'
                        id='domestic'
                        label='Domestic Account'
                      />
                      <RadioGroupItemWithLabel value={VERIFICATION_ACCOUNT_TYPE.iban} data-testid='iban' id='iban' label='IBAN' />
                      <RadioGroupItemWithLabel
                        value={VERIFICATION_ACCOUNT_TYPE.swift}
                        data-testid='swift'
                        id='swift'
                        label='SWIFT/BIC'
                      />
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {accountType === VERIFICATION_ACCOUNT_TYPE.domestic ? (
              <>
                <FormField
                  control={form.control}
                  name='country'
                  render={({ field }): JSX.Element => (
                    <FormItem data-testid='domestic-country'>
                      <FormLabel>Country</FormLabel>
                      <FormControl>
                        <Combobox
                          options={SUPPORTED_VERIFICATION_DOMESTIC_COUNTRIES_ARRAY.map((code) => ({
                            value: getCountryByCountryCode(code)?.name ?? code,
                            label: getCountryByCountryCode(code)?.name ?? code,
                          })).sort((a, b) => a.label.localeCompare(b.label))}
                          value={field.value}
                          onValueChange={(value): void => {
                            setCountryCode(value);
                            field.onChange(value);
                          }}
                          placeholder='Select a country...'
                          searchPlaceholder='Search country...'
                          emptyText='No country found.'
                          className='w-full'
                          buttonClassName='w-full justify-between'
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 items-start'>
                  {dynamicDomesticFields.map(({ key, config }) => (
                    <FormField
                      data-testid={`domestic-${key}`}
                      control={form.control}
                      key={key}
                      name={key as any}
                      render={({ field }): JSX.Element => (
                        <FormItem>
                          <FormLabel>{config.displayName}</FormLabel>
                          <FormControl>
                            <Input placeholder={config.displayName} {...field} type={config.inputType} className='!text-black' />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </>
            ) : accountType === VERIFICATION_ACCOUNT_TYPE.iban ? (
              <>
                <FormField
                  data-testid='iban'
                  control={form.control}
                  name='iban'
                  render={({ field }): JSX.Element => (
                    <FormItem>
                      <FormLabel>IBAN</FormLabel>
                      <FormControl>
                        <Input
                          data-testid='iban-input'
                          placeholder='IBAN'
                          {...field}
                          className='!text-black'
                          icon={
                            countryInfo && (
                              <div className='flex items-center gap-2 px-2 py-1 space-between'>
                                <span className='text-muted-foreground'>{countryInfo?.name}</span>
                                <div className='w-4 h-4 rounded-full overflow-hidden bg-muted flex items-center justify-center'>
                                  <Flag code={ibanCountry} className='w-4 h-4 object-cover' />
                                </div>
                              </div>
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            ) : accountType === VERIFICATION_ACCOUNT_TYPE.swift ? (
              <div className='space-y-6'>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 items-start'>
                  <FormField
                    data-testid='swift-bic'
                    control={form.control}
                    name='swiftBic'
                    render={({ field }): JSX.Element => (
                      <FormItem>
                        <FormLabel>SWIFT/BIC</FormLabel>
                        <FormControl>
                          <Input
                            placeholder='SWIFT/BIC'
                            {...field}
                            className='!text-black'
                            icon={
                              countryInfo && (
                                <div className='flex items-center gap-2 px-2 py-1 space-between'>
                                  <span className='text-muted-foreground'>{countryInfo?.name}</span>
                                  <div className='w-4 h-4 rounded-full overflow-hidden bg-muted flex items-center justify-center'>
                                    <Flag code={swiftCountry} className='w-4 h-4 object-cover' />
                                  </div>
                                </div>
                              )
                            }
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    data-testid='swift-account-number'
                    control={form.control}
                    name='accountNumber'
                    render={({ field }): JSX.Element => (
                      <FormItem>
                        <FormLabel>Account</FormLabel>
                        <FormControl>
                          <Input placeholder='Account' {...field} className='!text-black' />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            ) : null}

            <div className='flex justify-end mt-6'>
              <Button
                data-testid='search-button'
                type='submit'
                variant='default'
                disabled={isSubmitting}
                style={{ minWidth: 100 }}
              >
                {isSubmitting ? (
                  <span className='animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block'></span>
                ) : (
                  'Search'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
